/* eslint-disable react/no-danger, react/no-unescaped-entities, max-len */
import React from 'react';
import PropTypes from 'prop-types';
import { withPrefix } from 'gatsby';
import track from 'react-tracking';
import {
  Button, Badge, Spinner,
} from '@edx/paragon';
import { Parallax } from 'react-parallax';
import breakpoints from '@edx/paragon/dist/utils/breakpoints';
import classNames from 'classnames';
import { injectIntl, FormattedMessage } from 'gatsby-plugin-react-intl';
import { getBreadcrumbsByPath } from '@build-utils/helpers/breadcrumbsHelper';

import { intlShape } from '@prospectus/common/types/intlShape';
import ProgramSubscriptionModal from './ProgramSubscriptionModal';
import { USER_SUBSCRIPTION_STATES } from '../../../utils/constants';
import Breadcrumbs from '../../common/ui/PageTitleBreadcrumbs';

class ProgramHeader extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isMobileWindow: true,
      hasMounted: false,
      isSubscriptionModalVisible: false,
    };
    this.handleResize = this.handleResize.bind(this);
    this.openSubscriptionModal = this.openSubscriptionModal.bind(this);
    this.closeSubscriptionModal = this.closeSubscriptionModal.bind(this);
  }

  componentDidMount() {
    this.handleResize();
    this.setHasMounted();
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize() {
    const { isMobileWindow } = this.state;
    const isMobileWindowRefresh = window.matchMedia(`(max-width: ${breakpoints.small.maxWidth}px)`).matches;

    if (isMobileWindow !== isMobileWindowRefresh) {
      this.setState({
        isMobileWindow: !isMobileWindow,
      });
    }
  }

  getHeaderBackgroundImage() {
    const { primarySubject } = this.props;

    const subjectSlug = primarySubject.slug.toLowerCase();
    let subjectFolder = 'data-science';
    if (subjectSlug === 'computer-science' || subjectSlug === 'business-management') {
      subjectFolder = subjectSlug;
    }

    const { isMobileWindow } = this.state;
    const imageSize = isMobileWindow ? 'sm' : 'lg';

    return `/images/experiments/ProgramDetails/${subjectFolder}/hook-background-${imageSize}.jpg`;
  }

  setHasMounted() {
    this.setState({ hasMounted: true });
  }

  openSubscriptionModal() {
    this.setState({ isSubscriptionModalVisible: true });
  }

  closeSubscriptionModal() {
    this.setState({ isSubscriptionModalVisible: false });
  }

  render() {
    const {
      title,
      programHook,
      marketingSlug,
      subscriptionDetails,
      isSubscriptionLoading,
    } = this.props;

    const { hasMounted } = this.state;

    const { isSubscriptionEnabled, subscriptionStatus } = subscriptionDetails;

    const backgroundImage = hasMounted ? withPrefix(this.getHeaderBackgroundImage()) : '';

    const getSubscriptionButtonVariant = () => {
      if (isSubscriptionLoading) {
        return <Spinner animation="border" className="subscription-spinner" />;
      }

      if (subscriptionStatus === USER_SUBSCRIPTION_STATES.ACTIVE) {
        return (
          <FormattedMessage
            id="prospectus.program.header.subscription.modal.active.state"
            description="To pop up the Subscription Modal"
            defaultMessage="Start learning now"
          > {text => <div className="px-6">{text}</div>}
          </FormattedMessage>
        );
      }
      return (
        <FormattedMessage
          id="prospectus.program.header.subscription.modal.inactive.state"
          description="To pop up the Subscription Modal"
          defaultMessage="Start learning for free"
        > {text => <div className="px-6">{text}</div>}
        </FormattedMessage>
      );
    };

    const breadCrumbs = getBreadcrumbsByPath(marketingSlug, title);

    return (
      <Parallax
        blur={0}
        bgImage={backgroundImage}
        bgImageAlt=""
        strength={600}
      >

        <header className="program-header">

          <div className="container mw-lg program-header-container">
            <div className={
              classNames(
                'header-breadcrumbs',
                isSubscriptionEnabled ? 'mb-5' : 'ml-2',
              )
            }
            >
              <Breadcrumbs breadCrumbs={breadCrumbs} pageTitle={title} textColor="dark" />
            </div>
            {
              isSubscriptionEnabled && (
                <Badge variant="warning">
                  <FormattedMessage
                    id="prospectus.program.header.subscription.tag"
                    description="Subscription Tag above course heading"
                    defaultMessage="Program Subscription"
                  />
                </Badge>
              )
            }

            <h1 className="display-3">{programHook}</h1>

            {isSubscriptionEnabled && (

              <div className="subscription-detail-holder">
                <Button
                  variant="inverse-primary"
                  className="subscription-header-button"
                  onClick={this.openSubscriptionModal}
                >
                  {getSubscriptionButtonVariant()}
                </Button>
              </div>
            )}
            <ProgramSubscriptionModal
              subscriptionDetails={subscriptionDetails}
              isOpen={this.state.isSubscriptionModalVisible}
              closeModal={this.closeSubscriptionModal}
              enrollButtonId={this.props.enrollButtonId}
              {...this.props}
            />
          </div>
        </header>
      </Parallax>
    );
  }
}

ProgramHeader.propTypes = {
  title: PropTypes.string.isRequired,
  marketingSlug: PropTypes.string.isRequired,
  authoringOrganizations: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    key: PropTypes.string,
    logoImageUrl: PropTypes.string,
  })),
  intl: intlShape.isRequired,
  primarySubject: PropTypes.shape({
    name: PropTypes.string,
    slug: PropTypes.string,
  }).isRequired,
  subscriptionDetails: PropTypes.shape({
    isSubscriptionEnabled: PropTypes.bool,
    subscriptionStatus: PropTypes.string,
  }),
  isSubscriptionLoading: PropTypes.bool,
  enrollButtonId: PropTypes.string,
  programHook: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]).isRequired,
};

ProgramHeader.defaultProps = {
  authoringOrganizations: [],
  subscriptionDetails: {
    isSubscriptionEnabled: false,
    subscriptionStatus: '',
  },
  isSubscriptionLoading: false,
  enrollButtonId: '',
};

export default track({
  component: 'program-details-header',
})(injectIntl(ProgramHeader));
